// Date range presets, interval selection and previous-period math for the
// Analytics dashboard. Pure functions; timezone-aware where the calendar
// matters (Today).

import { startOfDayInTz } from './analyticsTimezone';

export const MAX_RANGE_DAYS = 30;
export const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export const RANGE_PRESETS = [
	{ key: 'today', label: 'Today', days: null },
	{ key: '7d', label: 'Last 7 days', days: 7 },
	{ key: '28d', label: 'Last 28 days', days: 28 },
	{ key: '30d', label: 'Last 30 days', days: 30 },
	{ key: 'custom', label: 'Custom', days: null },
];

export const DEFAULT_RANGE_PRESET = '28d';

export function isRangePreset(key) {
	return RANGE_PRESETS.some((p) => p.key === key);
}

/**
 * Resolve a preset (or custom from/to) into concrete instants. Presets are
 * anchored at `now`; the result is frozen by the caller so the same window is
 * reused for every request (cache keys must not drift between calls).
 *
 * @returns {{ preset: string, from: Date, to: Date }}
 */
export function resolveRange({ preset = DEFAULT_RANGE_PRESET, from = null, to = null, now = new Date(), tz = 'UTC' }) {
	const end = new Date(now.getTime());
	if (preset === 'custom' && from && to) {
		let f = new Date(from);
		let t = new Date(to);
		if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) {
			return resolveRange({ preset: DEFAULT_RANGE_PRESET, now, tz });
		}
		if (f > t) [f, t] = [t, f];
		if (t > end) t = end;
		if (t.getTime() - f.getTime() > MAX_RANGE_MS) f = new Date(t.getTime() - MAX_RANGE_MS);
		return { preset: 'custom', from: f, to: t };
	}
	if (preset === 'today') {
		return { preset, from: startOfDayInTz(end, tz), to: end };
	}
	const def = RANGE_PRESETS.find((p) => p.key === preset && p.days) || RANGE_PRESETS.find((p) => p.key === DEFAULT_RANGE_PRESET);
	return { preset: def.key, from: new Date(end.getTime() - def.days * DAY_MS), to: end };
}

/**
 * Bitmovin interval that keeps the response under the 200-row cap:
 *   <= 3h  -> MINUTE (max 180 rows)
 *   <= 48h -> HOUR   (max 48 rows)
 *   else   -> DAY    (max 30 rows for the 30-day cap)
 */
export function pickInterval(from, to) {
	const span = new Date(to).getTime() - new Date(from).getTime();
	if (span <= 3 * HOUR_MS) return 'MINUTE';
	if (span <= 48 * HOUR_MS) return 'HOUR';
	return 'DAY';
}

export function intervalMs(interval) {
	if (interval === 'MINUTE') return 60000;
	if (interval === 'HOUR') return HOUR_MS;
	return DAY_MS;
}

/**
 * Previous window for comparison:
 *   - Today: yesterday up to the same time of day (shift by 24h).
 *   - Everything else: the equal-length window immediately preceding.
 */
export function previousPeriod(range) {
	const from = new Date(range.from).getTime();
	const to = new Date(range.to).getTime();
	if (range.preset === 'today') {
		return { from: new Date(from - DAY_MS), to: new Date(to - DAY_MS) };
	}
	const span = to - from;
	return { from: new Date(from - span), to: new Date(from) };
}

/**
 * Chip suffix: "vs previous 28 days", "vs yesterday", "vs previous period".
 */
export function compareLabel(range) {
	if (range.preset === 'today') return 'vs yesterday';
	const def = RANGE_PRESETS.find((p) => p.key === range.preset && p.days);
	if (def) return `vs previous ${def.days} days`;
	const days = Math.round((new Date(range.to) - new Date(range.from)) / DAY_MS);
	if (days >= 2) return `vs previous ${days} days`;
	return 'vs previous period';
}

export function rangeLabel(range, formatDate) {
	const def = RANGE_PRESETS.find((p) => p.key === range.preset);
	if (def && def.key !== 'custom') return def.label;
	if (typeof formatDate === 'function') return `${formatDate(range.from)} to ${formatDate(range.to)}`;
	return 'Custom range';
}

/**
 * Serialize the range for the URL query. Presets are stored by key only so a
 * refresh re-anchors them at "now"; custom ranges store both instants.
 */
export function rangeToQuery(range) {
	if (range.preset === 'custom') {
		return { range: 'custom', from: new Date(range.from).toISOString(), to: new Date(range.to).toISOString() };
	}
	return { range: range.preset, from: undefined, to: undefined };
}

export function rangeFromQuery(query, { now = new Date(), tz = 'UTC' } = {}) {
	const preset = isRangePreset(query.range) ? query.range : DEFAULT_RANGE_PRESET;
	if (preset === 'custom') {
		return resolveRange({ preset, from: query.from, to: query.to, now, tz });
	}
	return resolveRange({ preset, now, tz });
}

/**
 * Sum / max helpers for [timestamp, value] series.
 */
export function seriesSum(series) {
	return (series || []).reduce((acc, point) => acc + (Number(point?.[1]) || 0), 0);
}

export function seriesMax(series) {
	return (series || []).reduce((acc, point) => Math.max(acc, Number(point?.[1]) || 0), 0);
}
